import { UncoveredList } from './Uncovered'
import { useState } from 'react'
import { translate, type Lang } from './i18n'
import type { PendingRevision, RevisionItem } from './api'
import { fmtTime } from './time'

/**
 * La reoptimización que espera aprobación, en el Centro de Mando (§15.1, §15.3).
 *
 * §15.1: «la autoridad de turno revisa el delta, aprueba y publica». Solo se podía en Planificación,
 * que no es donde está el jefe de turno durante un incidente. Aquí se ve lo que cambia contra el plan
 * activo —qué se retira a quién, qué se agrega, qué unidades quedaron fuera y cuánto cambia la
 * cobertura— sin comparar planes completos. El operador la ve porque la pidió; decidirla es del
 * jefe de turno.
 */
/** Cuántas visitas de cada lista se ven sin pedir más. */
const PRIMERAS = 5

export function RevisionPanel({
  lang, revision, canDecide, busy, onApprove, onReject, problem = null, onRequestAgain,
}: {
  lang: Lang
  revision: PendingRevision
  canDecide: boolean
  busy: boolean
  /**
   * Por qué no se pudo decidir, junto a los botones. El rechazo iba a la cabecera y el refresco
   * siguiente lo borraba: el jefe apretaba «Aprobar» y no pasaba nada visible (H-39).
   */
  problem?: string | null
  /**
   * Pedir otra corrida con el mismo motivo, para cuando esta quedó vieja: con el tiempo las visitas
   * se liberan y la revisión deja de corresponder al plan (H-40).
   */
  onRequestAgain?: () => void
  onApprove: (acknowledgePartial: boolean) => void
  onReject: (reason: string) => void
}) {
  const [acepto, setAcepto] = useState(false)
  const [motivo, setMotivo] = useState('')
  const hora = (iso: string) =>
    fmtTime(iso, lang)
  // Para decidir basta el resumen y las primeras de cada lista; el resto, a pedido. En la
  // demostración una revisión con 105 visitas canceladas ocupaba varias pantallas y escondía los
  // botones de decidir.
  const [abiertas, setAbiertas] = useState<ReadonlySet<string>>(new Set())
  const lista = (clave: string, items: RevisionItem[]) => {
    if (items.length === 0) return null
    const todas = abiertas.has(clave)
    const vistas = todas ? items : items.slice(0, PRIMERAS)
    return (
      <div>
        <strong>{translate(lang, clave, [items.length])}</strong>
        <ul className="meta">
          {vistas.map((i, n) => (
            <li key={`${i.callSign}-${i.label}-${n}`}>{i.callSign} · {i.label} · {hora(i.plannedArrivalAt)}</li>
          ))}
        </ul>
        {items.length > PRIMERAS && (
          <button onClick={() => setAbiertas((a) => {
            const b = new Set(a)
            if (todas) b.delete(clave); else b.add(clave)
            return b
          })}>
            {translate(lang, todas ? 'uncovered.fewer' : 'uncovered.all', [items.length - PRIMERAS])}
          </button>
        )}
      </div>
    )
  }

  return (
    <section className="panel">
      <h2>{translate(lang, 'revision.title', [revision.version])}</h2>
      <p className="notice">
        {translate(lang, 'revision.requested', [revision.requestedBy, hora(revision.requestedAt), revision.reason])}
      </p>
      <div className="card warning">
        <div className="meta">
          {translate(lang, 'revision.coverage', [revision.coverageBefore.toFixed(1), revision.coverageAfter.toFixed(1)])}
          {' · '}{translate(lang, 'revision.kept', [revision.frozen, revision.keptPlanned])}
        </div>
        {/* Las que no iniciaron el turno van aparte y resumidas: con la regla de H-127 pueden ser
            decenas, y ciento diecisiete renglones «(Fuera de servicio)» tapaban a la que de verdad
            se quedó fuera por una avería o un incidente. */}
        {revision.excludedUnits.some((u) => u.status !== 'OffDuty') && (
          <div className="meta">
            {translate(lang, 'revision.excluded', [
              revision.excludedUnits.filter((u) => u.status !== 'OffDuty')
                .map((u) => `${u.callSign} (${translate(lang, `unit.${u.status}`)})`).join(', '),
            ])}
          </div>
        )}
        {revision.excludedUnits.some((u) => u.status === 'OffDuty') && (
          <div className="meta">
            {translate(lang, 'revision.notStarted', [String(revision.excludedUnits.filter((u) => u.status === 'OffDuty').length),
              resumen(revision.excludedUnits.filter((u) => u.status === 'OffDuty').map((u) => u.callSign), lang)])}
          </div>
        )}
        {lista('revision.cancelDisclosed', revision.cancelDisclosed)}
        {lista('revision.reassign', revision.reassignInterrupted)}
        {lista('revision.removed', revision.removed)}
        {lista('revision.added', revision.added)}
      </div>

      {revision.sameAsReturned && (
        // La corrida después de una devolución puede proponer lo mismo: se dice, con la objeción
        // de entonces, para no revisar dos veces la misma lista sin saberlo (H-42).
        <div className="card warning">
          <p className="notice">
            {translate(lang, 'revision.sameAsReturned', [
              revision.sameAsReturned.version,
              revision.sameAsReturned.returnedBy || '—',
              revision.sameAsReturned.note || '—',
            ])}
          </p>
        </div>
      )}
      {revision.staleReason && (
        <div className="card warning">
          <p className="notice">{translate(lang, 'revision.stale', [revision.staleReason])}</p>
          {onRequestAgain && (
            <button className="primary" disabled={busy} onClick={onRequestAgain}>
              {translate(lang, 'revision.requestAgain')}
            </button>
          )}
        </div>
      )}
      {canDecide ? (
        <div className="card">
          {revision.approverMustBeOther && (
            <p className="notice warning">{translate(lang, 'revision.otherApprover')}</p>
          )}
          {revision.isPartial && <UncoveredList lang={lang} items={revision.uncovered} />}
          {!revision.approverMustBeOther && !revision.staleReason && revision.isPartial && (
            <label>
              <input type="checkbox" checked={acepto} disabled={busy} onChange={(e) => setAcepto(e.target.checked)} />
              {' '}{translate(lang, 'revision.acceptPartial')}
            </label>
          )}
          {!revision.approverMustBeOther && !revision.staleReason && (
            <div className="actions">
              <button
                className="primary"
                disabled={busy || (revision.isPartial && !acepto)}
                onClick={() => onApprove(revision.isPartial && acepto)}
              >
                {translate(lang, 'planner.approve')}
              </button>
            </div>
          )}
          <div className="row-line" style={{ flexWrap: 'wrap' }}>
            <input
              className="grow" value={motivo} disabled={busy}
              placeholder={translate(lang, 'planner.rejectReason')} aria-label={translate(lang, 'planner.rejectReason')}
              onChange={(e) => setMotivo(e.target.value)}
            />
            <button disabled={busy || motivo.trim() === ''} onClick={() => { onReject(motivo.trim()); setMotivo('') }}>
              {translate(lang, 'planner.reject')}
            </button>
          </div>
          {problem && <p className="error" role="alert">{problem}</p>}
        </div>
      ) : (
        <p className="notice">{translate(lang, 'revision.waiting')}</p>
      )}
    </section>
  )
}

/** Los primeros indicativos y cuántos más: la lista completa no cabe ni se lee. */
export function resumen(callSigns: string[], lang: Lang, cuantos = 10): string {
  const vistos = callSigns.slice(0, cuantos).join(', ')
  return callSigns.length <= cuantos ? vistos : translate(lang, 'revision.andMore', [vistos, String(callSigns.length - cuantos)])
}
